import { Component, Input, Output, EventEmitter, Self, Optional, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-input-select',
  templateUrl: './input-select.component.html',
  styleUrls: ['./input-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class InputSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() name = '';
  @Input() showLabel = true;
  @Input() showPlaceholder = true;
  @Input() showError = true;
  @Input() showSearch = true;
  @Input('data') set _data(value: any[]) {
    this.data = value;
    this.filteredData = value;
  }
  @Input() titleKey!: string;
  @Input() valueKey!: string;
  @Output() focus: EventEmitter<FocusEvent> = new EventEmitter();
  @Output() blur: EventEmitter<FocusEvent> = new EventEmitter();
  @Output() selected: EventEmitter<any> = new EventEmitter();

  private subscription$: Subscription = new Subscription();
  private innerValue!: string;
  public disabled = false;
  public search$: Subject<string> = new Subject();
  public data!: any[];
  public filteredData!: any[];

  constructor(@Self() @Optional() public ngControl: NgControl) {
    ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.subscription$.add(
      this.search$
        .pipe(distinctUntilChanged(), debounceTime(300))
        .subscribe((value: string) => (this.filteredData = this.data.filter((el) => el[this.titleKey].toLowerCase().includes(value.toLowerCase())))),
    );
  }

  get option(): string | null {
    if (this.data) {
      const el = this.data.find((el) => el[this.valueKey] == this.innerValue);
      if (el) {
        return el[this.titleKey];
      }
    }
    return null;
  }

  get hasError(): boolean {
    if (this.showError && this.ngControl.control?.touched && this.ngControl.control?.invalid) {
      return true;
    }
    return false;
  }

  get value(): string {
    return this.innerValue;
  }

  set value(value: string) {
    this.writeValue(value);
    this.onChange(value);
    this.onTouch(value);
  }

  public writeValue(value: string): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.selected.emit(this.data.find((el) => el[this.valueKey] == value));
    }
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange: any = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouch: any = () => {};

  ngOnDestroy(): void {
    this.subscription$?.unsubscribe();
  }
}
